import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useHighlightedEvents } from '../hooks/useHighlightedEvents';

import { FEATURED_SLIDER_AUTOPLAY_DELAY, FEATURED_SLIDER_FADE_DURATION } from '../constants';
import { CustomGlassBox } from './CustomGlassBox';
import styles from './FeaturedSlider.module.scss';
import { FeaturedSliderImages } from './FeaturedSliderImages';
import { FeaturedSliderItem } from './FeaturedSliderItem';

export const FeaturedSlider = () => {
  const { events, isLoading } = useHighlightedEvents();
  const [activeIndex, setActiveIndex] = useState(0);
  const [fadingOutIndex, setFadingOutIndex] = useState<number | null>(null);
  const [isBackwardFade, setIsBackwardFade] = useState(false);
  const isBackwardFadeRef = useRef(false);

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: FEATURED_SLIDER_AUTOPLAY_DELAY,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    []
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      duration: 30,
      skipSnaps: false,
      containScroll: 'trimSnaps',
      dragFree: false,
    },
    [autoplayPlugin]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    // Skip if backward fade in progress (we handle activeIndex manually)
    if (isBackwardFadeRef.current) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const onSettle = useCallback(() => {
    // Restart autoplay after any scroll settles
    if (!isBackwardFadeRef.current) {
      autoplayPlugin.play();
    }
  }, [autoplayPlugin]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on('select', onSelect);
    emblaApi.on('settle', onSettle);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('settle', onSettle);
    };
  }, [emblaApi, onSelect, onSettle]);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      autoplayPlugin.stop();

      const isBackward = index < activeIndex;

      if (isBackward) {
        // Backward: use fade animation
        isBackwardFadeRef.current = true;
        setIsBackwardFade(true);
        setFadingOutIndex(activeIndex);
        setActiveIndex(index);

        setTimeout(() => {
          emblaApi.scrollTo(index, true); // Jump without animation
          isBackwardFadeRef.current = false;
          setIsBackwardFade(false);
          setFadingOutIndex(null);
          autoplayPlugin.play();
        }, FEATURED_SLIDER_FADE_DURATION);
      } else {
        // Forward: normal Embla slide
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi, autoplayPlugin, activeIndex]
  );

  if (isLoading || events.length === 0) {
    return null;
  }

  return (
    <CustomGlassBox className="tw-flex tw-flex-col tw-p-[20px] lg:tw-flex-row lg:tw-pl-[40px]" borderRadius="20px">
      <div className={styles.emblaWrapper}>
        <div ref={emblaRef} className={`${styles.emblaViewport} ${isBackwardFade ? styles.backwardFade : ''}`}>
          <div className={styles.emblaContainer}>
            {events.map((event, index) => (
              <div
                key={event.id}
                className={styles.emblaSlide}
                data-active={index === activeIndex}
                data-fading-out={index === fadingOutIndex}
              >
                <FeaturedSliderItem event={event} />
              </div>
            ))}
          </div>
        </div>

        <div className={styles.pagination}>
          {events.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`${styles.dot} ${index === activeIndex ? styles.dotActive : ''}`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="tw-absolute tw-right-[100px] tw-top-[20px] tw-hidden lg:tw-block">
        <FeaturedSliderImages
          events={events}
          emblaApi={emblaApi}
          manualTransition={
            isBackwardFade ? { activeIndex, previousIndex: fadingOutIndex ?? 0, isForward: false } : null
          }
        />
      </div>
    </CustomGlassBox>
  );
};
